import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useProductSubmission } from "@/hooks/useProductSubmission";
import { BaseProductFields } from "./BaseProductFields";
import { PoultryFields } from "./PoultryFields";
import { useSubscriptionLimit } from "@/hooks/useSubscriptionLimit";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  quantity: z.string().min(1, "La quantité est requise"),
  unit: z.string().min(1, "L'unité est requise"),
  customUnit: z.string().optional(),
  price: z.string().min(1, "Le prix est requis"),
  minQuantity: z.string().min(1, "La quantité minimale est requise"),
  Age: z.string().optional(),
  Santé: z.string().optional(),
  Poids: z.string().optional(),
  Régime: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface AddProductFormProps {
  onSubmit: (product: any) => void;
  onCancel: () => void;
  isPoultry?: boolean;
}

export const AddProductForm = ({ onSubmit, onCancel, isPoultry = false }: AddProductFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      quantity: "0",
      unit: "",
      customUnit: "",
      price: "0",
      minQuantity: "0",
      Age: "",
      Santé: "",
      Poids: "",
      Régime: "",
    },
  });

  const { isLoading, handleSubmit: submitProduct } = useProductSubmission(onSubmit);
  const { checkProductLimit } = useSubscriptionLimit();

  // Fetch current product count
  const { data: currentCount = 0 } = useQuery({
    queryKey: ["stockItemsCount"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { data: businessStocks } = await supabase
        .from('business_stocks')
        .select('id')
        .eq('owner_id', user.id);

      if (!businessStocks?.length) return 0;

      const stockIds = businessStocks.map(bs => bs.id);

      const { count } = await supabase
        .from('stock_items')
        .select('*', { count: 'exact', head: true })
        .in('stock_id', stockIds);

      return count || 0;
    },
  });

  const onFormSubmit = async (values: FormValues) => {
    console.log("Form values:", values);
    
    const canAddProduct = await checkProductLimit(currentCount);
    if (!canAddProduct) {
      return;
    }

    const success = await submitProduct(
      values.name,
      values.quantity,
      values.unit,
      values.customUnit,
      values.price,
      values.minQuantity,
      isPoultry,
      isPoultry ? {
        Age: values.Age,
        Santé: values.Santé,
        Poids: values.Poids,
        Régime: values.Régime,
      } : undefined
    );

    if (success) {
      form.reset();
      onSubmit(values);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-6">
        {isPoultry ? (
          <PoultryFields form={form} />
        ) : (
          <BaseProductFields form={form} />
        )}

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Ajout en cours..." : "Ajouter"}
          </Button>
        </div>
      </form>
    </Form>
  );
};